import Button from '@components/v2/ui/Button/Button';
import Icon from '@components/v2/ui/Icon/Icon';
import Menu from '@components/v2/ui/Menu/Menu';
import MenuItem from '@components/v2/ui/Menu/MenuItem';
import Tooltip from '@components/v2/ui/Tooltip/Tooltip';
import Typography from '@components/v2/ui/Typography/Typography';
import { Stack } from '@mui/material';
import { ChevronDown } from 'lucide-react';
import { useState } from 'react';
import LangEnum from '../../../../../lang/LangEnum';
import { useTranslation } from '../../../../../modules/translation/TranslationContext';
import { TabItem } from '../tabs.types';
import { getTabSecondaryLabelTestId } from '../tabs.utils';

interface TabsOverflowMenuProps {
  items: TabItem[];
  selectedId: string;
  onSelect: (id: string) => void;
}

export function TabsOverflowMenu({ items, selectedId, onSelect }: TabsOverflowMenuProps) {
  const { translate } = useTranslation();
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);

  const hasActiveItem = items.some((item) => item.id === selectedId);

  const handleSelect = (id: string) => {
    onSelect(id);
    setAnchorEl(null);
  };

  return (
    <>
      <Button
        variant="contained"
        color="secondary"
        shape="round"
        isActive={hasActiveItem}
        endIcon={<Icon icon={ChevronDown} size="md" />}
        sx={{ flexShrink: 0, whiteSpace: 'nowrap' }}
        onClick={(event) => setAnchorEl(event.currentTarget)}
      >
        {translate(LangEnum.GLOBAL_MORE)}
      </Button>
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        onClose={() => setAnchorEl(null)}
      >
        {items.map((item) => (
          <MenuItem
            key={item.id}
            selected={item.id === selectedId}
            testId={item.testId}
            onClick={() => handleSelect(item.id)}
          >
            <Tooltip interactive placement="bottom" tabIndex={-1} TooltipContent={item.tooltip}>
              <Stack direction="row" sx={{ alignItems: 'center', flex: 1 }}>
                <Typography variant="subtitle2" sx={{ flex: 1 }}>
                  {translate(item.label as LangEnum)}
                </Typography>
                {item.secondaryLabel !== undefined && (
                  <Typography
                    variant="subtitle2"
                    sx={{ ml: 2, color: 'text.disabled' }}
                    testId={item.testId ? getTabSecondaryLabelTestId(item.testId) : undefined}
                  >
                    {item.secondaryLabel}
                  </Typography>
                )}
                {item.endIcon}
              </Stack>
            </Tooltip>
          </MenuItem>
        ))}
      </Menu>
    </>
  );
}
